import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import uuid from 'uuid-random';
import type { IWedding_RSVP } from '@bridebook/models/source/models/Weddings.types';
import { addedOrEditedRsvpQuestionAnalytics } from '@bridebook/web/lib/rsvp/analytics/actions';
import type { RsvpQuestionFormValues } from '@bridebook/web/lib/rsvp/types';
import compact from 'lodash/compact';
import type { Event } from '@bridebook/web/lib/events/types';
import { useGetIsPopularRsvpQuestions } from '@bridebook/web/lib/rsvp/hooks/use-get-is-popular-rsvp-questions';

interface IArgs {
  id: string | string[] | undefined;
  isNewEntry: boolean;
  weddingRsvp: IWedding_RSVP | null | undefined;
  updateWeddingRsvpAsync: (data: Partial<IWedding_RSVP>) => Promise<Partial<IWedding_RSVP>>;
  eventsCount: number;
  unifiedEvents: Event[];
  onSuccess: () => void;
}

export const useRsvpFormSubmit = ({
  id,
  isNewEntry,
  weddingRsvp,
  updateWeddingRsvpAsync,
  eventsCount,
  unifiedEvents,
  onSuccess,
}: IArgs) => {
  const dispatch = useDispatch();
  const isPopularRsvpQuestion = useGetIsPopularRsvpQuestions();

  return useCallback(
    async (data: RsvpQuestionFormValues) => {
      if (typeof id !== 'string' || !weddingRsvp) return;

      const isList = data.type === 'list';

      const shared = isList
        ? // lists must have options set, otherwise the type doesn't work properly
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          { ...data, options: data.options! }
        : { question: data.question, type: data.type, events: data.events };

      const updatedQuestions: IWedding_RSVP['questions'] = isNewEntry
        ? [...weddingRsvp.questions, { id: uuid(), ...shared }]
        : weddingRsvp.questions.map((q: any) => (q.id === id ? { id, ...shared } : q));

      const eventsNames = compact(
        data.events.map((e) => unifiedEvents.find((ue) => e === ue.id)?.name),
      );

      await updateWeddingRsvpAsync({ ...weddingRsvp, questions: updatedQuestions });
      dispatch(
        addedOrEditedRsvpQuestionAnalytics({
          action: isNewEntry ? 'added' : 'edited',
          type: isPopularRsvpQuestion(shared) ? 'popular' : 'custom',
          question: shared.question,
          events: data.events.length === eventsCount ? 'allEvents' : 'selectEvents',
          eventsList: eventsNames,
          ...(isList
            ? {
                answerType: 'selection',
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                selectionList: data.options!,
              }
            : {
                answerType: data.type === 'boolean' ? 'yesNo' : 'longText',
              }),
        }),
      );
      onSuccess();
    },
    [
      id,
      weddingRsvp,
      isNewEntry,
      updateWeddingRsvpAsync,
      dispatch,
      eventsCount,
      onSuccess,
      unifiedEvents,
    ],
  );
};
